import { z } from 'zod';

/**
 * Data Source Kind Enum
 */
export const dataSourceKindSchema = z.enum(['Cube']);
export type DataSourceKind = z.infer<typeof dataSourceKindSchema>;

/**
 * Mapping of connection providers to valid data source kinds they can produce.
 */
export const connectionProviderToDataSourceKinds: Record<string, DataSourceKind[]> = {
  CUBEJS: ['Cube'],
};

/**
 * Metric Type
 */
export const metricTypeSchema = z.enum(['number', 'string']);
export type MetricType = z.infer<typeof metricTypeSchema>;

/**
 * Field Type
 */
export const fieldTypeSchema = z.enum(['number', 'string', 'time']);
export type FieldType = z.infer<typeof fieldTypeSchema>;

/**
 * Data Source Metric Schema
 */
export const dataSourceMetricSchema = z.object({
  id: z.uuid(),
  dataSourceId: z.uuid(),
  sourceIdentifier: z.string(),
  name: z.string(),
  description: z.string(),
  examples: z.array(z.string()).default([]),
  metricType: metricTypeSchema,
  sourceMetadata: z.record(z.string(), z.unknown()).default({}),
  isDeleted: z.boolean(),
  isVisible: z.boolean(),
  isFilterable: z.boolean(),
  isSortable: z.boolean(),
  createdAt: z.coerce.date(),
  lastSyncedAt: z.coerce.date().nullable(),
});
export type DataSourceMetric = z.infer<typeof dataSourceMetricSchema>;

/**
 * Data Source Field Schema
 */
export const dataSourceFieldSchema = z.object({
  id: z.uuid(),
  dataSourceId: z.uuid(),
  sourceIdentifier: z.string(),
  name: z.string(),
  description: z.string(),
  examples: z.array(z.string()).default([]),
  fieldType: fieldTypeSchema,
  sourceMetadata: z.record(z.string(), z.unknown()).default({}),
  isDeleted: z.boolean(),
  isVisible: z.boolean(),
  isFilterable: z.boolean(),
  isSortable: z.boolean(),
  caseInsensitive: z.boolean().nullable(),
  createdAt: z.coerce.date(),
  lastSyncedAt: z.coerce.date().nullable(),
});
export type DataSourceField = z.infer<typeof dataSourceFieldSchema>;

export const dataSourceRelationshipCardinalitySchema = z.enum([
  'one_to_one',
  'one_to_many',
  'many_to_one',
  'many_to_many',
]);
export type DataSourceRelationshipCardinality = z.infer<typeof dataSourceRelationshipCardinalitySchema>;

export const dataSourceRelationshipJoinPreferenceSchema = z.enum(['inner', 'left']);
export type DataSourceRelationshipJoinPreference = z.infer<typeof dataSourceRelationshipJoinPreferenceSchema>;

export const dataSourceRelationshipFieldOperatorSchema = z.enum(['and', 'or']);
export type DataSourceRelationshipFieldOperator = z.infer<typeof dataSourceRelationshipFieldOperatorSchema>;

export const dataSourceRelationshipFieldMappingSchema = z.object({
  id: z.uuid(),
  sourceFieldId: z.uuid(),
  sourceFieldName: z.string(),
  sourceFieldIdentifier: z.string(),
  relatedFieldId: z.uuid(),
  relatedFieldName: z.string(),
  relatedFieldIdentifier: z.string(),
  ordinal: z.number().int().positive(),
});
export type DataSourceRelationshipFieldMapping = z.infer<typeof dataSourceRelationshipFieldMappingSchema>;

export const dataSourceRelationshipSchema = z.object({
  id: z.uuid(),
  sourceId: z.uuid(),
  relatedSourceId: z.uuid(),
  relatedSourceName: z.string(),
  relatedSourceIdentifier: z.string(),
  cardinality: dataSourceRelationshipCardinalitySchema,
  joinPreference: dataSourceRelationshipJoinPreferenceSchema,
  fieldOperator: dataSourceRelationshipFieldOperatorSchema,
  label: z.string().nullable(),
  isEnabled: z.boolean(),
  isSystemManaged: z.boolean(),
  // True when this row is system-managed AND its endpoint pair (within the
  // same data connection) is shared with at least one other system-managed
  // (`isDeleted = false`) row AND the group is not cleanly disambiguated by
  // its labels. A group is considered resolved (flag = false) iff every row
  // has a non-null label AND the labels are all distinct; any null label or
  // any duplicate label flags every row in the group. False for
  // operator-authored rows and for any system row with no siblings.
  // Derived server-side.
  needsLabelDisambiguation: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  fieldMappings: z.array(dataSourceRelationshipFieldMappingSchema),
});
export type DataSourceRelationship = z.infer<typeof dataSourceRelationshipSchema>;

export const listDataSourceRelationshipsResponseSchema = z.object({
  items: z.array(dataSourceRelationshipSchema),
});
export type ListDataSourceRelationshipsResponse = z.infer<typeof listDataSourceRelationshipsResponseSchema>;

export const dataSourceRelationshipFieldInputSchema = z.object({
  sourceFieldId: z.uuid(),
  relatedFieldId: z.uuid(),
  ordinal: z.number().int().positive(),
});
export type DataSourceRelationshipFieldInput = z.infer<typeof dataSourceRelationshipFieldInputSchema>;

export const createDataSourceRelationshipBodySchema = z
  .object({
    relatedSourceId: z.uuid(),
    cardinality: dataSourceRelationshipCardinalitySchema,
    joinPreference: dataSourceRelationshipJoinPreferenceSchema,
    fieldOperator: dataSourceRelationshipFieldOperatorSchema.optional(),
    label: z.string().nullable().optional(),
    isEnabled: z.boolean().optional(),
    fieldMappings: z.array(dataSourceRelationshipFieldInputSchema).min(1),
  })
  .superRefine((value, ctx) => {
    const seenOrdinals = new Set<number>();
    for (const mapping of value.fieldMappings) {
      if (mapping.sourceFieldId === mapping.relatedFieldId) {
        ctx.addIssue({
          code: 'custom',
          path: ['fieldMappings'],
          message: 'A relationship field mapping must reference two different fields',
        });
      }

      if (seenOrdinals.has(mapping.ordinal)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fieldMappings'],
          message: 'Relationship field mapping ordinals must be unique',
        });
      }
      seenOrdinals.add(mapping.ordinal);
    }
  });
export type CreateDataSourceRelationshipBody = z.infer<typeof createDataSourceRelationshipBodySchema>;

export const updateDataSourceRelationshipBodySchema = z
  .object({
    cardinality: dataSourceRelationshipCardinalitySchema.optional(),
    joinPreference: dataSourceRelationshipJoinPreferenceSchema.optional(),
    fieldOperator: dataSourceRelationshipFieldOperatorSchema.optional(),
    label: z.string().nullable().optional(),
    isEnabled: z.boolean().optional(),
    fieldMappings: z.array(dataSourceRelationshipFieldInputSchema).min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.cardinality === undefined &&
      value.joinPreference === undefined &&
      value.fieldOperator === undefined &&
      value.label === undefined &&
      value.isEnabled === undefined &&
      value.fieldMappings === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one field must be provided',
      });
    }

    if (value.fieldMappings) {
      const seenOrdinals = new Set<number>();
      for (const mapping of value.fieldMappings) {
        if (mapping.sourceFieldId === mapping.relatedFieldId) {
          ctx.addIssue({
            code: 'custom',
            path: ['fieldMappings'],
            message: 'A relationship field mapping must reference two different fields',
          });
        }

        if (seenOrdinals.has(mapping.ordinal)) {
          ctx.addIssue({
            code: 'custom',
            path: ['fieldMappings'],
            message: 'Relationship field mapping ordinals must be unique',
          });
        }
        seenOrdinals.add(mapping.ordinal);
      }
    }
  });
export type UpdateDataSourceRelationshipBody = z.infer<typeof updateDataSourceRelationshipBodySchema>;

/**
 * Data Connection Source Schema
 */
export const dataConnectionSourceSchema = z.object({
  id: z.uuid(),
  dataConnectionId: z.uuid(),
  sourceIdentifier: z.string(),
  kind: dataSourceKindSchema,
  name: z.string(),
  description: z.string(),
  queryHint: z.string().nullable(),
  temporalAnchorFieldId: z.uuid().nullable(),
  temporalAnchorMode: z.enum(['max', 'min', 'none']),
  grain: z.unknown().nullable(),
  isDeleted: z.boolean(),
  createdAt: z.coerce.date(),
  lastSyncedAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date(),
  lastUpdatedBy: z.uuid().nullable(),
  metrics: z.array(dataSourceMetricSchema),
  fields: z.array(dataSourceFieldSchema),
});
export type DataConnectionSource = z.infer<typeof dataConnectionSourceSchema>;

/**
 * List Data Connection Sources Response
 */
export const listDataConnectionSourcesResponseSchema = z.object({
  items: z.array(dataConnectionSourceSchema),
});
export type ListDataConnectionSourcesResponse = z.infer<typeof listDataConnectionSourcesResponseSchema>;

/**
 * Data Connection Source Summary Schema (lightweight — for list endpoint; no full metrics/fields arrays)
 */
export const dataConnectionSourceSummarySchema = z.object({
  id: z.uuid(),
  dataConnectionId: z.uuid(),
  sourceIdentifier: z.string(),
  kind: dataSourceKindSchema,
  name: z.string(),
  description: z.string(),
  queryHint: z.string().nullable(),
  temporalAnchorFieldId: z.uuid().nullable(),
  temporalAnchorMode: z.enum(['max', 'min', 'none']),
  grain: z.unknown().nullable(),
  isDeleted: z.boolean(),
  createdAt: z.coerce.date(),
  lastSyncedAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date(),
  lastUpdatedBy: z.uuid().nullable(),
  metricsCount: z.number(),
  fieldsCount: z.number(),
  relationshipsCount: z.number(),
});
export type DataConnectionSourceSummary = z.infer<typeof dataConnectionSourceSummarySchema>;

/**
 * List Data Connection Sources Summary Response (lightweight, for populating list views)
 */
export const listDataConnectionSourcesSummaryResponseSchema = z.object({
  items: z.array(dataConnectionSourceSummarySchema),
});
export type ListDataConnectionSourcesSummaryResponse = z.infer<
  typeof listDataConnectionSourcesSummaryResponseSchema
>;

/**
 * Introspected Data Connection Source Schema
 */
export const introspectedDataConnectionSourceSchema = z.object({
  sourceIdentifier: z.string(),
  name: z.string(),
  description: z.string(),
});
export type IntrospectedDataConnectionSource = z.infer<typeof introspectedDataConnectionSourceSchema>;

/**
 * List Introspected Data Connection Sources Response
 */
export const listIntrospectedDataConnectionSourcesResponseSchema = z.object({
  items: z.array(introspectedDataConnectionSourceSchema),
});
export type ListIntrospectedDataConnectionSourcesResponse = z.infer<
  typeof listIntrospectedDataConnectionSourcesResponseSchema
>;

/**
 * Update Data Connection Source Body
 */
export const updateDataConnectionSourceBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    queryHint: z.string().nullable().optional(),
    temporalAnchorFieldId: z.uuid().nullable().optional(),
    temporalAnchorMode: z.enum(['max', 'min', 'none']).optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.description !== undefined ||
      value.queryHint !== undefined ||
      value.temporalAnchorFieldId !== undefined ||
      value.temporalAnchorMode !== undefined,
    {
      message: 'At least one field must be provided',
    }
  );
export type UpdateDataConnectionSourceBody = z.infer<typeof updateDataConnectionSourceBodySchema>;

export const updateDataSourceMetricBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    examples: z.array(z.string()).optional(),
    isVisible: z.boolean().optional(),
    isFilterable: z.boolean().optional(),
    isSortable: z.boolean().optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.description !== undefined ||
      value.examples !== undefined ||
      value.isVisible !== undefined ||
      value.isFilterable !== undefined ||
      value.isSortable !== undefined,
    {
      message: 'At least one field must be provided',
    }
  );
export type UpdateDataSourceMetricBody = z.infer<typeof updateDataSourceMetricBodySchema>;

export const updateDataSourceFieldBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    examples: z.array(z.string()).optional(),
    isVisible: z.boolean().optional(),
    isFilterable: z.boolean().optional(),
    isSortable: z.boolean().optional(),
    caseInsensitive: z.boolean().nullable().optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      value.description !== undefined ||
      value.examples !== undefined ||
      value.isVisible !== undefined ||
      value.isFilterable !== undefined ||
      value.isSortable !== undefined ||
      value.caseInsensitive !== undefined,
    {
      message: 'At least one field must be provided',
    }
  );
export type UpdateDataSourceFieldBody = z.infer<typeof updateDataSourceFieldBodySchema>;

export const dataConnectionSourceBatchUpdateItemSchema = z.object({
  sourceId: z.uuid(),
  name: z.string().trim().min(1),
  description: z.string(),
});
export type DataConnectionSourceBatchUpdateItem = z.infer<typeof dataConnectionSourceBatchUpdateItemSchema>;

export const updateDataConnectionSourcesBatchBodySchema = z.object({
  items: z.array(dataConnectionSourceBatchUpdateItemSchema).min(1),
});
export type UpdateDataConnectionSourcesBatchBody = z.infer<typeof updateDataConnectionSourcesBatchBodySchema>;

export const dataSourceMetricBatchUpdateItemSchema = z.object({
  metricId: z.uuid(),
  name: z.string().trim().min(1),
  description: z.string(),
  examples: z.array(z.string()).optional(),
});
export type DataSourceMetricBatchUpdateItem = z.infer<typeof dataSourceMetricBatchUpdateItemSchema>;

export const updateDataSourceMetricsBatchBodySchema = z.object({
  items: z.array(dataSourceMetricBatchUpdateItemSchema).min(1),
});
export type UpdateDataSourceMetricsBatchBody = z.infer<typeof updateDataSourceMetricsBatchBodySchema>;

export const dataSourceFieldBatchUpdateItemSchema = z.object({
  fieldId: z.uuid(),
  name: z.string().trim().min(1),
  description: z.string(),
  examples: z.array(z.string()).optional(),
});
export type DataSourceFieldBatchUpdateItem = z.infer<typeof dataSourceFieldBatchUpdateItemSchema>;

export const updateDataSourceFieldsBatchBodySchema = z.object({
  items: z.array(dataSourceFieldBatchUpdateItemSchema).min(1),
});
export type UpdateDataSourceFieldsBatchBody = z.infer<typeof updateDataSourceFieldsBatchBodySchema>;

export const batchUpdateMetadataResponseSchema = z.object({
  updatedCount: z.number().int().nonnegative(),
});
export type BatchUpdateMetadataResponse = z.infer<typeof batchUpdateMetadataResponseSchema>;

/**
 * Sync Data Connection Request
 */
export const syncDataConnectionRequestSchema = z.object({
  selectedSourceIdentifiers: z.array(z.string()).optional(),
});
export type SyncDataConnectionRequest = z.infer<typeof syncDataConnectionRequestSchema>;

/**
 * Sync Data Connection Sources Response
 */
export const syncDataConnectionResponseSchema = z.object({
  sourcesCreated: z.number(),
  sourcesUpdated: z.number(),
  sourcesDeleted: z.number(),
  relationshipsCreated: z.number(),
  relationshipsUpdated: z.number(),
  relationshipsClaimed: z.number(),
  relationshipsUnclaimed: z.number(),
  relationshipsDeleted: z.number(),
  relationshipsSkippedMissingNeighbor: z.number(),
  relationshipsSkippedMissingField: z.number(),
  relationshipsSkippedCardinalityConflict: z.number(),
  lastSyncedAt: z.coerce.date(),
});
export type SyncDataConnectionResponse = z.infer<typeof syncDataConnectionResponseSchema>;

export const importDataConnectionSourceMetricSchema = z.object({
  sourceIdentifier: z.string(),
  name: z.string(),
  description: z.string(),
  examples: z.array(z.string()).default([]),
  metricType: metricTypeSchema,
  sourceMetadata: z.record(z.string(), z.unknown()).default({}),
  isVisible: z.boolean(),
  isFilterable: z.boolean(),
  isSortable: z.boolean(),
});
export type ImportDataConnectionSourceMetric = z.infer<typeof importDataConnectionSourceMetricSchema>;

export const importDataConnectionSourceFieldSchema = z.object({
  sourceIdentifier: z.string(),
  name: z.string(),
  description: z.string(),
  examples: z.array(z.string()).default([]),
  fieldType: fieldTypeSchema,
  sourceMetadata: z.record(z.string(), z.unknown()).default({}),
  isVisible: z.boolean(),
  isFilterable: z.boolean(),
  isSortable: z.boolean(),
});
export type ImportDataConnectionSourceField = z.infer<typeof importDataConnectionSourceFieldSchema>;

export const importDataConnectionSourceSchema = z.object({
  sourceIdentifier: z.string(),
  grain: z.unknown().nullable(),
  name: z.string(),
  description: z.string(),
  queryHint: z.string().nullable().optional(),
  temporalAnchorFieldId: z.string().nullable().optional(),
  temporalAnchorMode: z.enum(['max', 'min', 'none']).optional(),
  kind: dataSourceKindSchema,
  metrics: z.array(importDataConnectionSourceMetricSchema),
  fields: z.array(importDataConnectionSourceFieldSchema),
});
export type ImportDataConnectionSource = z.infer<typeof importDataConnectionSourceSchema>;

export const importDataConnectionSourcesRequestSchema = z.object({
  dataSources: z.array(importDataConnectionSourceSchema),
});
export type ImportDataConnectionSourcesRequest = z.infer<typeof importDataConnectionSourcesRequestSchema>;

export const importDataConnectionSourcesResponseSchema = z.object({
  sourcesCreated: z.number(),
  sourcesUpdated: z.number(),
  metricsCreated: z.number(),
  metricsUpdated: z.number(),
  fieldsCreated: z.number(),
  fieldsUpdated: z.number(),
  lastSyncedAt: z.coerce.date(),
});
export type ImportDataConnectionSourcesResponse = z.infer<typeof importDataConnectionSourcesResponseSchema>;

export const exportDataConnectionSourcesResponseSchema = z.object({
  dataSources: z.array(importDataConnectionSourceSchema),
});
export type ExportDataConnectionSourcesResponse = z.infer<typeof exportDataConnectionSourcesResponseSchema>;
